import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-xs hover:bg-primary/90",
        destructive:
          "bg-destructive/80 text-white shadow-xs hover:bg-destructive focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/50 dark:hover:bg-destructive/70",
        outline:
          "border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50",
        secondary:
          "bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80",
        ghost:
          "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
        link: "text-primary underline-offset-4 hover:underline",
        /** Pies de ABM: Guardar filled muted green, ícono blanco (WCAG AA vs #047857). Hover oscurece el fill. */
        saveMuted:
          "bg-emerald-700 text-white shadow-xs hover:bg-emerald-900 hover:text-white hover:ring-2 hover:ring-emerald-900/35 focus-visible:ring-emerald-700/30 dark:bg-emerald-800 dark:text-white dark:hover:bg-emerald-950 dark:hover:ring-emerald-400/25 [&_svg]:text-white",
        /** Pies de ABM: Eliminar filled muted red, ícono blanco (WCAG AA vs #b91c1c). Hover oscurece el fill. */
        dangerMuted:
          "bg-red-700 text-white shadow-xs hover:bg-red-900 hover:text-white hover:ring-2 hover:ring-red-900/35 focus-visible:ring-red-700/30 dark:bg-red-800 dark:text-white dark:hover:bg-red-950 dark:hover:ring-red-400/25 [&_svg]:text-white",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        icon: "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot : "button"

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    />
  )
}

/** Ghost/icon delete: muted red (íconos chicos de tablas · notas · Estado de cuenta). */
export const deleteIconButtonClass =
  "text-destructive/70 hover:text-destructive hover:bg-destructive/10"

export { Button, buttonVariants }
